import React, { useEffect, useRef, useState } from 'react';
import './App.css';

const COLS = 10;
const ROWS = 20;
const EMPTY = 0;
const TETROMINOES = {
  I: [
    [[0,1],[1,1],[2,1],[3,1]],
    [[2,0],[2,1],[2,2],[2,3]],
    [[0,2],[1,2],[2,2],[3,2]],
    [[1,0],[1,1],[1,2],[1,3]],
  ],
  J: [
    [[0,0],[0,1],[1,1],[2,1]],
    [[1,0],[2,0],[1,1],[1,2]],
    [[0,1],[1,1],[2,1],[2,2]],
    [[1,0],[1,1],[0,2],[1,2]],
  ],
  L: [
    [[2,0],[0,1],[1,1],[2,1]],
    [[1,0],[1,1],[1,2],[2,2]],
    [[0,1],[1,1],[2,1],[0,2]],
    [[0,0],[1,0],[1,1],[1,2]],
  ],
  O: [
    [[1,0],[2,0],[1,1],[2,1]],
    [[1,0],[2,0],[1,1],[2,1]],
    [[1,0],[2,0],[1,1],[2,1]],
    [[1,0],[2,0],[1,1],[2,1]],
  ],
  S: [
    [[1,0],[2,0],[0,1],[1,1]],
    [[1,0],[1,1],[2,1],[2,2]],
    [[1,1],[2,1],[0,2],[1,2]],
    [[0,0],[0,1],[1,1],[1,2]],
  ],
  T: [
    [[1,0],[0,1],[1,1],[2,1]],
    [[1,0],[1,1],[2,1],[1,2]],
    [[0,1],[1,1],[2,1],[1,2]],
    [[1,0],[0,1],[1,1],[1,2]],
  ],
  Z: [
    [[0,0],[1,0],[1,1],[2,1]],
    [[2,0],[1,1],[2,1],[1,2]],
    [[0,1],[1,1],[1,2],[2,2]],
    [[1,0],[0,1],[1,1],[0,2]],
  ],
};
const COLORS = {
  0: '#23272f',
  I: '#00f0f0',
  J: '#0000f0',
  L: '#f0a000',
  O: '#f0f000',
  S: '#00f000',
  T: '#a000f0',
  Z: '#f00000',
};
const TETROMINO_KEYS = Object.keys(TETROMINOES);

function randomTetromino() {
  const type = TETROMINO_KEYS[Math.floor(Math.random() * TETROMINO_KEYS.length)];
  return { type, rotation: 0, x: 3, y: 0 };
}

function getCells(tetromino) {
  return TETROMINOES[tetromino.type][tetromino.rotation].map(([dx, dy]) => [tetromino.x + dx, tetromino.y + dy]);
}

function isValid(board, tetromino) {
  return getCells(tetromino).every(([x, y]) =>
    x >= 0 && x < COLS && y >= 0 && y < ROWS && board[y][x] === EMPTY
  );
}

function placeTetromino(board, tetromino) {
  const newBoard = board.map(row => [...row]);
  getCells(tetromino).forEach(([x, y]) => {
    if (y >= 0 && y < ROWS && x >= 0 && x < COLS) newBoard[y][x] = tetromino.type;
  });
  return newBoard;
}

function clearLines(board) {
  const newBoard = board.filter(row => row.some(cell => cell === EMPTY));
  const linesCleared = ROWS - newBoard.length;
  while (newBoard.length < ROWS) newBoard.unshift(Array(COLS).fill(EMPTY));
  return { board: newBoard, linesCleared };
}

function App() {
  const [board, setBoard] = useState(() => Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY)));
  const [current, setCurrent] = useState(randomTetromino());
  const [next, setNext] = useState(randomTetromino());
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [running, setRunning] = useState(false);
  const interval = useRef();
  // Calculate the max cell size that fits both width and height, using border-box for cells
  const maxWidth = window.innerWidth * 0.9;
  const maxHeight = window.innerHeight * 0.85;
  const cellSize = Math.floor(Math.min(maxWidth / COLS, maxHeight / ROWS));
  const gridWidth = cellSize * COLS;
  const gridHeight = Math.floor(cellSize * ROWS * 1.1);

  useEffect(() => {
    if (!running || gameOver) return;
    if (interval.current) clearInterval(interval.current);
    interval.current = setInterval(() => {
      setCurrent(curr => {
        // Try to move down, if not possible, place piece
        const moved = { ...curr, y: curr.y + 1 };
        if (isValid(board, moved)) {
          return moved;
        } else {
          // Place piece
          const newBoard = placeTetromino(board, curr);
          const { board: cleared, linesCleared } = clearLines(newBoard);
          setScore(s => s + linesCleared * 100);
          const nextTet = next;
          setCurrent(nextTet);
          setNext(randomTetromino());
          setBoard(cleared);
          if (!isValid(cleared, nextTet)) {
            setGameOver(true);
            setRunning(false);
          }
          return nextTet;
        }
      });
    }, 300);
    return () => clearInterval(interval.current);
    // eslint-disable-next-line
  }, [running, gameOver, board, next]);

  useEffect(() => {
    const handle = e => {
      if (gameOver || !running) return;
      if (e.key === 'ArrowLeft') {
        setCurrent(curr => {
          const moved = { ...curr, x: curr.x - 1 };
          return isValid(board, moved) ? moved : curr;
        });
      }
      if (e.key === 'ArrowRight') {
        setCurrent(curr => {
          const moved = { ...curr, x: curr.x + 1 };
          return isValid(board, moved) ? moved : curr;
        });
      }
      if (e.key === 'ArrowDown') {
        setCurrent(curr => {
          const moved = { ...curr, y: curr.y + 1 };
          if (isValid(board, moved)) return moved;
          // Place piece if can't move down
          const newBoard = placeTetromino(board, curr);
          const { board: cleared, linesCleared } = clearLines(newBoard);
          setScore(s => s + linesCleared * 100);
          const nextTet = next;
          setCurrent(nextTet);
          setNext(randomTetromino());
          setBoard(cleared);
          if (!isValid(cleared, nextTet)) {
            setGameOver(true);
            setRunning(false);
          }
          return nextTet;
        });
      }
      if (e.key === 'ArrowUp') {
        setCurrent(curr => {
          const rotated = { ...curr, rotation: (curr.rotation + 1) % 4 };
          return isValid(board, rotated) ? rotated : curr;
        });
      }
      if (e.key === ' ' || e.key === 'Spacebar') {
        setCurrent(curr => {
          let dropped = { ...curr };
          while (isValid(board, { ...dropped, y: dropped.y + 1 })) {
            dropped.y += 1;
          }
          // Place piece
          const newBoard = placeTetromino(board, dropped);
          const { board: cleared, linesCleared } = clearLines(newBoard);
          setScore(s => s + linesCleared * 100);
          const nextTet = next;
          setCurrent(nextTet);
          setNext(randomTetromino());
          setBoard(cleared);
          if (!isValid(cleared, nextTet)) {
            setGameOver(true);
            setRunning(false);
          }
          return nextTet;
        });
      }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
    // eslint-disable-next-line
  }, [running, gameOver, board, next]);

  function reset() {
    setBoard(Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY)));
    setCurrent(randomTetromino());
    setNext(randomTetromino());
    setScore(0);
    setGameOver(false);
    setRunning(false);
  }

  // Draw board with current piece
  const displayBoard = board.map(row => [...row]);
  if (!gameOver) {
    getCells(current).forEach(([x, y]) => {
      if (y >= 0 && y < ROWS && x >= 0 && x < COLS) displayBoard[y][x] = current.type;
    });
  }

  return (
    <div className="App" style={{ paddingTop: 0, minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 32, flex: 1, alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: gridHeight }}>
          <div
            className="grid"
            style={{
              width: gridWidth,
              height: gridHeight,
              background: '#23272f',
              margin: '0 auto',
              boxSizing: 'content-box',
              display: 'grid',
              gridTemplateColumns: `repeat(${COLS}, 1fr)`,
              gridTemplateRows: `repeat(${ROWS}, 1fr)`,
              overflow: 'hidden',
            }}
          >
            {displayBoard.map((row, i) =>
              row.map((cell, j) => (
                <div
                  key={`${i}-${j}`}
                  className="cell"
                  style={{
                    width: cellSize,
                    height: cellSize,
                    background: COLORS[cell] || COLORS[0],
                    border: `1px solid #2c313a`,
                    boxSizing: 'border-box',
                  }}
                />
              ))
            )}
          </div>
          <div style={{ marginTop: 16, color: '#aaa', fontSize: '1.1rem' }}>
            Score: {score}
          </div>
          {gameOver && <div style={{ color: '#f00', fontWeight: 700, marginTop: 8 }}>Game Over</div>}
        </div>
        <div style={{ minWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={{ marginBottom: 8, color: '#aaa' }}>Next:</div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 24px)',
              width: 96,
              height: 96,
              background: '#23272f',
              marginBottom: 16,
            }}
          >
            {Array.from({ length: 4 * 4 }).map((_, idx) => {
              const x = idx % 4;
              const y = Math.floor(idx / 4);
              const isFilled = TETROMINOES[next.type][0].some(([dx, dy]) => dx === x && dy === y);
              return (
                <div
                  key={idx}
                  style={{
                    width: 24,
                    height: 24,
                    background: isFilled ? COLORS[next.type] : COLORS[0],
                    border: '1px solid #2c313a',
                  }}
                />
              );
            })}
          </div>
          <div style={{ marginBottom: 16, color: '#aaa', fontSize: '1.1rem' }}>
            Score: {score}
          </div>
          <button onClick={() => setRunning(r => !r)} style={{ marginBottom: 8 }}>
            {running ? 'Pause' : 'Start'}
          </button>
          <button onClick={reset}>Reset</button>
          <div style={{ marginTop: 24, color: '#888', fontSize: '0.95rem', maxWidth: 180 }}>
            Controls: ← → ↓ to move, ↑ to rotate, Space for hard drop
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
